/**
 * ALOHA PLATFORM - Voice-Enabled 3D Avatar Chat
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Mic, MicOff, Volume2, VolumeX, Send, Loader2 } from 'lucide-react';
import AvatarRenderer3D from './AvatarRenderer3D';
import EnhancedAvatarRenderer3D from './EnhancedAvatarRenderer3D';
import RealisticHumanAvatar from './RealisticHumanAvatar';
import ProfessionalAvatar from './ProfessionalAvatar';
import UltraRealisticAvatar from './UltraRealisticAvatar';
import ProfessionalRealisticAvatar from './ProfessionalRealisticAvatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoiceAvatarChat = ({ token, sessionId, onSessionUpdate, selectedAvatar = 'queen_supernova' }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentEmotion, setCurrentEmotion] = useState('neutral');
  const [lipSyncData, setLipSyncData] = useState({ intensity: 0 });
  const [visemeData, setVisemeData] = useState(null);
  const [useElevenLabs, setUseElevenLabs] = useState(false);
  
  const recognitionRef = useRef(null);
  const audioRef = useRef(new Audio());
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Initialize Web Speech API
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputText(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    // Initialize Audio Context for lip sync
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 256;

    // Cleanup audio on unmount
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Voice input toggle
  const toggleVoiceInput = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  // Send message to avatar
  const sendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = { type: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/avatar/chat`,
        {
          message: text.trim(),
          session_id: sessionId,
          avatar_id: selectedAvatar || 'queen_supernova',
          voice_config: {
            voice: 'nova', // OpenAI voice (warm, approachable female)
            use_elevenlabs: useElevenLabs,
            stability: 0.5,
            similarity_boost: 0.75,
            avatar_type: selectedAvatar || 'queen_supernova' // Pass selected avatar
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = response.data;
      setCurrentEmotion(data.emotion);
      
      const avatarMessage = {
        type: 'avatar',
        text: data.text,
        emotion: data.emotion,
        animation: data.avatar_animation
      };
      
      setMessages(prev => [...prev, avatarMessage]);
      
      if (onSessionUpdate) {
        onSessionUpdate(data.session_id);
      }

      // Play real audio with lip sync
      if (!isMuted && data.voice_audio) {
        await playAudioWithLipSync(data.voice_audio, data.lip_sync_data);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        type: 'error',
        text: 'Failed to get response. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Play real audio with synchronized lip sync
  const playAudioWithLipSync = async (audioBase64, lipSync) => {
    try {
      // Convert base64 to audio blob
      const audioData = atob(audioBase64);
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const view = new Uint8Array(arrayBuffer);
      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i);
      }
      const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(blob);
      
      // Set audio source
      audioRef.current.src = audioUrl;
      
      // Set viseme data for lip sync
      setVisemeData(lipSync);
      
      // Audio event handlers
      audioRef.current.onplay = () => {
        setIsSpeaking(true);
      };
      
      audioRef.current.onended = () => {
        setIsSpeaking(false);
        setLipSyncData({ intensity: 0 });
        setVisemeData(null);
        URL.revokeObjectURL(audioUrl);
      };
      
      audioRef.current.onerror = (e) => {
        console.error('Audio playback error:', e);
        setIsSpeaking(false);
        setLipSyncData({ intensity: 0 });
        setVisemeData(null);
      };
      
      // Play audio
      await audioRef.current.play();
      
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsSpeaking(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputText);
  };

  return (
    <div className="flex flex-col h-full">
      {/* 3D Avatar Display */}
      <div className="mb-4">
        <ProfessionalRealisticAvatar 
          avatarType={selectedAvatar}
          isPlaying={isSpeaking}
          lipSyncData={visemeData || lipSyncData}
          emotion={currentEmotion}
        />
        
        {/* Status Indicators */}
        <div className="flex items-center justify-center gap-4 mt-3">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
            isSpeaking ? 'bg-green-500/20 border-green-500' : 'bg-gray-500/20 border-gray-500'
          } border`}>
            <Volume2 className={`w-4 h-4 ${isSpeaking ? 'text-green-400 animate-pulse' : 'text-gray-400'}`} />
            <span className="text-white text-sm">
              {isSpeaking ? 'Speaking...' : 'Ready'}
            </span>
          </div>
          
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500">
            <div className="w-2 h-2 rounded-full bg-purple-400"></div>
            <span className="text-white text-sm">Emotion: {currentEmotion}</span>
          </div>
          
          <button
            onClick={() => setUseElevenLabs(!useElevenLabs)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full border transition ${
              useElevenLabs 
                ? 'bg-blue-500/20 border-blue-500' 
                : 'bg-gray-500/20 border-gray-500'
            }`}
            title={useElevenLabs ? 'Using ElevenLabs Voice' : 'Using OpenAI Voice'}
          >
            <span className="text-white text-sm">
              {useElevenLabs ? '🎙️ ElevenLabs' : '🔊 OpenAI'}
            </span>
          </button>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-4 max-h-64">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-md px-4 py-3 rounded-2xl ${
                msg.type === 'user'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : msg.type === 'error'
                  ? 'bg-red-500/20 border border-red-500 text-white'
                  : 'bg-white/10 text-white border border-white/20'
              }`}
            >
              {msg.type === 'avatar' && (
                <div className="text-xs text-gray-300 mb-1">Aloha Queen Agent</div>
              )}
              <p>{msg.text}</p>
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white/10 px-4 py-3 rounded-2xl border border-white/20 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
              <span className="text-white text-sm">Thinking...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Controls */}
      <form onSubmit={handleSubmit} className="flex items-center gap-3">
        {/* Voice Input Button */}
        <button
          type="button"
          onClick={toggleVoiceInput}
          className={`p-3 rounded-xl transition transform hover:scale-105 ${
            isListening 
              ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
              : 'bg-purple-600 hover:bg-purple-700'
          }`}
          disabled={loading}
          title={isListening ? 'Stop listening' : 'Start voice input'}
        >
          {isListening ? (
            <MicOff className="w-5 h-5 text-white" />
          ) : (
            <Mic className="w-5 h-5 text-white" />
          )}
        </button>

        {/* Text Input */}
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={isListening ? "Listening..." : "Type or speak your message..."}
          className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          disabled={loading || isListening}
        />

        {/* Mute Toggle */}
        <button
          type="button"
          onClick={() => {
            setIsMuted(!isMuted);
            if (!isMuted) {
              // Stop audio playback
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
              }
              setIsSpeaking(false);
              setLipSyncData({ intensity: 0 });
            }
          }}
          className="p-3 bg-blue-600 hover:bg-blue-700 rounded-xl transition"
          title={isMuted ? 'Unmute voice' : 'Mute voice'}
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5 text-white" />
          ) : (
            <Volume2 className="w-5 h-5 text-white" />
          )}
        </button>

        {/* Send Button */}
        <button
          type="submit"
          disabled={loading || !inputText.trim()}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-semibold transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Send className="w-5 h-5" />
          Send
        </button>
      </form>
    </div>
  );
};

export default VoiceAvatarChat;
